// IE Hub - Base Theme Tokens
// Foundation theme that all client packs extend

import { createTheme } from '@ui';

// Type definitions
interface ColorChannel {
  main: string;
  light?: string;
  dark?: string;
  contrastText?: string;
}

// Utility functions
const pxToRem = (px: number): string => `${px / 16}rem`;

const createPaletteChannel = (color: ColorChannel): Required<ColorChannel> => ({
  main: color.main,
  light: color.light || color.main,
  dark: color.dark || color.main,
  contrastText: color.contrastText || '#ffffff',
});

// Base color palette
const baseColors = {
  primary: createPaletteChannel({
    main: '#003F44',
    light: '#4A6B70',
    dark: '#001A1E',
    contrastText: '#ffffff'
  }),
  // Custom brand color for buttons and accents
  brand: createPaletteChannel({
    main: '#155F54',
    light: '#4A8B7F',
    dark: '#0D3E36',
    contrastText: '#ffffff'
  }),
  secondary: createPaletteChannel({
    main: '#E0E722',
    light: '#F0F266',
    dark: '#B8C01A',
    contrastText: '#000000'
  }),
  success: createPaletteChannel({
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff'
  }),
  warning: createPaletteChannel({
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff'
  }),
  error: createPaletteChannel({
    main: '#d32f2f',
    light: '#f44336',
    dark: '#c62828',
    contrastText: '#ffffff'
  }),
  info: createPaletteChannel({
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff'
  })
};

// Grey scale
const grey = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121'
};

// Base typography
const baseTypography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  h1: {
    fontSize: pxToRem(40),
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em'
  },
  h2: {
    fontSize: pxToRem(32),
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.01em'
  },
  h3: {
    fontSize: pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.4
  },
  h4: {
    fontSize: pxToRem(20),
    fontWeight: 600,
    lineHeight: 1.4
  },
  h5: {
    fontSize: pxToRem(18),
    fontWeight: 600,
    lineHeight: 1.5
  },
  h6: {
    fontSize: pxToRem(16),
    fontWeight: 600,
    lineHeight: 1.6
  },
  subtitle1: {
    fontSize: pxToRem(16),
    fontWeight: 500,
    lineHeight: 1.5
  },
  subtitle2: {
    fontSize: pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.5
  },
  body1: {
    fontSize: pxToRem(16),
    fontWeight: 400,
    lineHeight: 1.5
  },
  body2: {
    fontSize: pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5
  },
  caption: {
    fontSize: pxToRem(12),
    fontWeight: 400,
    lineHeight: 1.5
  },
  overline: {
    fontSize: pxToRem(12),
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  button: {
    fontSize: pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.5,
    textTransform: 'none'
  }
};

// Base theme structure
export const baseTheme = createTheme({
  palette: {
    mode: 'light',
    ...baseColors,
    grey,
    text: {
      primary: grey[900],
      secondary: grey[600],
      disabled: grey[400]
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff'
    },
    divider: grey[200],
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)'
    }
  },
  typography: baseTypography,
  shape: {
    borderRadius: 8
  },
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
  ]
});

export default baseTheme;
